import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Badge, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './BoardRemapModal.css';

const trimCat = s => String(s ?? '').trim();

/** @param {{ show: boolean, isSaving?: boolean, onHide: () => void, categories: Array<{ nombre: string, color?: string, filas: number, columnas: number }>, casillas: Array<object>, oobCasillas: Array<object>, onApply: (remap: Array<{ casillaId: string, fila: number, columna: number }>) => void | Promise<void> }} props */
export default function BoardRemapModal({
  show,
  isSaving = false,
  onHide,
  categories,
  casillas,
  oobCasillas,
  onApply,
}) {
  const { t } = useTranslation();
  const [selectedOobId, setSelectedOobId] = useState(null);
  /** @type {[Record<string, { fila: number, columna: number }>, Function]} */
  const [assignments, setAssignments] = useState({});

  useEffect(() => {
    if (show) {
      setSelectedOobId(null);
      setAssignments({});
    }
  }, [show, oobCasillas]);

  const normalizedCategories = useMemo(
    () =>
      categories.map(cat => ({
        ...cat,
        nombre: trimCat(cat.nombre),
        filas: Math.max(1, Number(cat.filas) || 1),
        columnas: Math.max(1, Number(cat.columnas) || 1),
      })),
    [categories]
  );

  const findCellCasilla = (catNombre, fila, columna) =>
    casillas.find(
      c => trimCat(c.categoria) === trimCat(catNombre) && c.fila === fila && c.columna === columna
    );

  const cellAssignment = (catNombre, fila, columna) => {
    for (const oob of oobCasillas) {
      const id = String(oob._id);
      const a = assignments[id];
      if (
        a &&
        a.fila === fila &&
        a.columna === columna &&
        trimCat(oob.categoria) === trimCat(catNombre)
      ) {
        return oob;
      }
    }
    return null;
  };

  const isTargetTaken = (catNombre, fila, columna, exceptCasillaId) => {
    for (const oob of oobCasillas) {
      if (String(oob._id) === String(exceptCasillaId)) continue;
      const a = assignments[String(oob._id)];
      if (
        a &&
        a.fila === fila &&
        a.columna === columna &&
        trimCat(oob.categoria) === trimCat(catNombre)
      ) {
        return true;
      }
    }
    return false;
  };

  const handleCellClick = (catNombre, fila, columna) => {
    if (isSaving) return;
    if (!selectedOobId) return;
    const oob = oobCasillas.find(c => String(c._id) === String(selectedOobId));
    if (!oob || trimCat(oob.categoria) !== trimCat(catNombre)) return;

    const cellCasilla = findCellCasilla(catNombre, fila, columna);
    if (!cellCasilla || cellCasilla.estado !== 'Disponible') return;
    if (isTargetTaken(catNombre, fila, columna, selectedOobId)) return;

    setAssignments(prev => ({
      ...prev,
      [String(selectedOobId)]: { fila, columna },
    }));
  };

  const clearAssignment = id => {
    if (isSaving) return;
    setAssignments(prev => {
      const next = { ...prev };
      delete next[String(id)];
      return next;
    });
  };

  const allPlaced = oobCasillas.every(c => assignments[String(c._id)]);

  const handleApply = async () => {
    if (!allPlaced || isSaving) return;
    const remap = oobCasillas.map(c => ({
      casillaId: c._id,
      fila: assignments[String(c._id)].fila,
      columna: assignments[String(c._id)].columna,
    }));
    await onApply(remap);
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        if (!isSaving) onHide();
      }}
      size='xl'
      backdrop='static'
      keyboard={!isSaving}
      centered
      scrollable
      dialogClassName='board-remap-modal-dialog'
      contentClassName='board-remap-modal-content'
    >
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>{t('raffle.boardRemapTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body className='board-remap-modal-body'>
        <p className='text-muted small mb-4'>{t('raffle.boardRemapHelp')}</p>

        {normalizedCategories.map(cat => {
          const catOob = oobCasillas.filter(c => trimCat(c.categoria) === cat.nombre);
          const selectionActiveForCat =
            Boolean(selectedOobId) && catOob.some(c => String(c._id) === String(selectedOobId));

          return (
            <div key={cat.nombre} className='board-remap__category'>
              <h6 className='board-remap__category-title' style={{ color: cat.color || '#333' }}>
                {cat.nombre}{' '}
                <small className='text-muted'>
                  ({cat.filas}×{cat.columnas})
                </small>
              </h6>
              <div className='board-remap__category-body'>
                <aside className='board-remap__sidebar'>
                  {catOob.length > 0 ? (
                    <>
                      <div className='fw-semibold small mb-2'>
                        {t('raffle.boardRemapReservations')}
                      </div>
                      <div className='board-remap__sidebar-list'>
                        {catOob.map(c => {
                          const sel = String(selectedOobId) === String(c._id);
                          const placed = Boolean(assignments[String(c._id)]);
                          return (
                            <div key={c._id} className='board-remap__reservation-row'>
                              <Button
                                size='sm'
                                variant={
                                  sel ? 'primary' : placed ? 'outline-success' : 'outline-secondary'
                                }
                                className='board-remap-reservation-btn w-100 text-start'
                                disabled={isSaving}
                                onClick={() => {
                                  if (!isSaving) setSelectedOobId(String(c._id));
                                }}
                              >
                                <span className='d-block text-truncate'>
                                  ({c.fila},{c.columna})
                                  <Badge
                                    bg={c.estado === 'Vendido' ? 'secondary' : 'warning'}
                                    className='ms-1'
                                  >
                                    {c.nombreComprador || c.estado}
                                  </Badge>
                                </span>
                              </Button>
                              {placed && (
                                <Button
                                  size='sm'
                                  variant='link'
                                  className='board-remap__clear-link p-0'
                                  disabled={isSaving}
                                  onClick={() => clearAssignment(c._id)}
                                >
                                  {t('raffle.boardRemapClear')}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {catOob.length > 0 && !selectionActiveForCat && (
                        <div className='small text-info mt-2'>
                          {t('raffle.boardRemapSelectReservation')}
                        </div>
                      )}
                    </>
                  ) : (
                    <p className='small text-muted mb-0 fst-italic'>
                      {t('raffle.boardRemapNoMovesHere')}
                    </p>
                  )}
                </aside>
                <div className='board-remap__grid-panel'>
                  <div
                    className='board-remap-grid'
                    style={{
                      gridTemplateColumns: `repeat(${cat.columnas}, minmax(40px, 52px))`,
                    }}
                  >
                    {Array.from({ length: cat.filas * cat.columnas }, (_, i) => {
                      const fila = Math.floor(i / cat.columnas);
                      const columna = i % cat.columnas;
                      const cellCasilla = findCellCasilla(cat.nombre, fila, columna);
                      const assignedOob = cellAssignment(cat.nombre, fila, columna);
                      const selected = oobCasillas.find(
                        o => String(o._id) === String(selectedOobId)
                      );
                      const canPick =
                        !isSaving &&
                        Boolean(selected) &&
                        trimCat(selected.categoria) === trimCat(cat.nombre) &&
                        cellCasilla?.estado === 'Disponible' &&
                        Boolean(selectedOobId) &&
                        !isTargetTaken(cat.nombre, fila, columna, selectedOobId);

                      let label = '·';
                      let cls = 'board-remap-cell';
                      if (assignedOob) {
                        label = '→';
                        cls += ' board-remap-cell--assigned';
                      } else if (cellCasilla) {
                        if (cellCasilla.estado === 'Vendido') {
                          label = 'V';
                          cls += ' board-remap-cell--sold';
                        } else if (cellCasilla.estado === 'Reservado') {
                          label = 'R';
                          cls += ' board-remap-cell--reserved';
                        } else {
                          label = '';
                          cls += canPick
                            ? ' board-remap-cell--empty board-remap-cell--pickable'
                            : ' board-remap-cell--empty';
                        }
                      }

                      return (
                        <button
                          key={`${cat.nombre}-${fila}-${columna}`}
                          type='button'
                          title={
                            cellCasilla
                              ? `${cellCasilla.estado} (${fila},${columna})`
                              : `(${fila},${columna})`
                          }
                          className={cls}
                          disabled={isSaving || (!canPick && !assignedOob)}
                          onClick={() => handleCellClick(cat.nombre, fila, columna)}
                        >
                          {assignedOob ? (
                            <span className='board-remap-cell__assign'>
                              {trimCat(assignedOob.categoria).slice(0, 1)}
                              {assignedOob.fila},{assignedOob.columna}
                            </span>
                          ) : (
                            label
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className='small text-muted mt-2 board-remap__legend'>
                    <span className='me-3'>
                      <span className='board-remap-legend board-remap-legend--empty' />{' '}
                      {t('raffle.boardRemapLegendEmpty')}
                    </span>
                    <span className='me-3'>
                      <span className='board-remap-legend board-remap-legend--r' /> R
                    </span>
                    <span>
                      <span className='board-remap-legend board-remap-legend--v' /> V
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </Modal.Body>
      <Modal.Footer className='board-remap-modal-footer'>
        <Button variant='secondary' disabled={isSaving} onClick={onHide}>
          {t('common.cancel')}
        </Button>
        <Button variant='primary' disabled={!allPlaced || isSaving} onClick={handleApply}>
          {isSaving ? (
            <>
              <Spinner
                as='span'
                animation='border'
                size='sm'
                role='status'
                className='me-2'
                aria-hidden='true'
              />
              {t('raffle.boardRemapSaving')}
            </>
          ) : (
            t('raffle.boardRemapApply')
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
